<template>
    <div class="w-full flex relative" :class="wrapperClasses">
        <label class="relative block w-full">
            <span class="block text-slate-800 text-lg font-medium mb-2 sr-only">
                Hotel Name
            </span>
            <div
                class="relative rounded flex items-center pr-2 w-full"
                :class="{
                    'ring-slate-800 ring-1': inputFocused,
                }"
            >
                <icon
                    icon="hotel"
                    svg-class="fill-none"
                    path-class="stroke-slate-800"
                    class="absolute left-2 top-1/2 -translate-y-1/2 z-10"
                    :width="24"
                    :height="24"
                />

                <input
                    type="text"
                    name="properties_query"
                    id="properties-query"
                    v-model="propertiesQuery"
                    class="border-none rounded w-full bg-white text-base block px-10 text-gray-900 placeholder:text-sm placeholder:text-gray-400 focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Hotel name"
                    v-debounce="350"
                    @input="propertiesInput"
                    @focus="updateFocus(true)"
                    @blur="updateFocus(false)"
                    @keydown.enter.prevent="enter()"
                    @keydown.down="down"
                    @keydown.up="up"
                    autocomplete="off"
                />
                <ClearInputButton
                    @clear="clearPropertySearch"
                    v-if="propertiesQuery"
                />
            </div>
        </label>
        <div
            class="absolute left-0 right-0 top-full pt-2 z-40"
            v-if="propertiesQuery && propertiesQuery.length > queryStartLength"
        >
            <ul
                id="property-dropdown"
                class="w-full max-h-40 min-h-16 overflow-y-auto p-2 bg-white rounded-lg shadow-sm border border-slate-200 relative"
            >
                <li
                    class="w-full flex items-center justify-center"
                    v-if="isSearching"
                >
                    <LoadingState :loading="true" />
                </li>
                <li
                    v-for="(prop, pIndex) in properties"
                    :key="`property-${pIndex}`"
                    @click="selectProperty(prop)"
                    class="w-full cursor-pointer p-2 hover:bg-slate-100 transition-colors gap-2 rounded-md flex items-center justify-between"
                    :class="{ '!bg-slate-200': prop.active }"
                    role="button"
                >
                    <span class="text-sm">
                        {{ Filters.decodeHtmlEntities(prop.name) }}
                    </span>
                </li>
                <li
                    class="w-full p-4 gap-2 rounded-md flex"
                    v-if="!isSearching && properties.length === 0"
                >
                    No results found, try adjusting your search.
                </li>
            </ul>
        </div>
    </div>
</template>
<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import Filters from '@/filters/index.js';
import Icon from '@/components/ui/icon/icon.vue';
import ClearInputButton from '@/components/ui/clear-input-button/clear-input-button.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';

const props = defineProps({
    wrapperClasses: {
        type: String,
        default: 'h-14 p-2 bg-white rounded-xl shadow',
    },
    url: {
        type: String,
        default: '/hotels/namesearch',
    },
    externalActions: {
        type: Boolean,
        default: false,
    },
});
const router = useRouter();
const emit = defineEmits(['propertyChanged', 'selectProperty']);
const propertiesQuery = ref(null);
const properties = ref([]);
const loadingProperties = ref(false);
const inputFocused = ref(false);
const activeIndex = ref(-1);
const queryStartLength = ref(3);
const scrollAmount = ref(42);
const isSearching = ref(false);

const scrollContainer = computed(() => {
    return document.getElementById('property-dropdown');
});

const propertiesInput = () => {
    if (
        propertiesQuery.value &&
        propertiesQuery.value.length > queryStartLength.value
    ) {
        isSearching.value = true;
        loadingProperties.value = true;
        ApiService.apiCall(props.url, 'post', {
            search: propertiesQuery.value,
        })
            .then((response) => {
                properties.value = response.data;
                loadingProperties.value = false;
                isSearching.value = false;
            })
            .catch(() => {
                loadingProperties.value = false;
                isSearching.value = false;
            });
    } else {
        properties.value = [];
        loadingProperties.value = false;
        isSearching.value = false;
    }
};

const clearPropertySearch = () => {
    propertiesQuery.value = null;
    properties.value = [];
    isSearching.value = false;
    emit('propertyChanged', null);
};

const updateFocus = (val) => {
    inputFocused.value = val;
};

const selectProperty = (prop) => {
    if (props.externalActions) {
        emit('selectProperty', prop);
        clearPropertySearch();
    } else {
        router.push(`/hotels/properties/${prop.slug}`);
    }
};

const enter = () => {
    const item = properties.value[activeIndex.value];
    if (props.externalActions) {
        emit('selectProperty', item);
        clearPropertySearch();
    } else {
        router.push(`/hotels/properties/${item.slug}`);
    }
};

const resetActiveItem = () => {
    properties.value.forEach((item) => {
        item.active = false;
    });
};

const up = () => {
    if (activeIndex.value >= 0) {
        if (activeIndex.value > 0) {
            activeIndex.value--;
        }

        resetActiveItem();
        properties.value[activeIndex.value].active = true;

        if (properties.value.length - activeIndex.value > 3) {
            scrollContainer.value.scrollTop =
                scrollContainer.value.scrollTop - scrollAmount.value;
        }
    }
};

const down = () => {
    if (
        activeIndex.value > -2 &&
        activeIndex.value + 1 < properties.value.length
    ) {
        activeIndex.value++;
        resetActiveItem();
        properties.value[activeIndex.value].active = true;

        if (activeIndex.value > 3) {
            scrollContainer.value.scrollTop =
                scrollAmount.value + scrollContainer.value.scrollTop;
        }
    }
};
</script>
